import {
  dialogFrame, formDialogBodyStyles, formDialogCancelStyles, formDialogCloseStyles, formDialogFieldStyles,
  formDialogFooterStyles, formDialogHeaderStyles, formDialogLabelStyles, formDialogPaperStyles,
  formDialogSubmitStyles, formDialogTitleStyles
} from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import {
  Box, Button, Dialog, FormControlLabel, FormHelperText, FormLabel, IconButton, Radio, RadioGroup, TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { authApiSlice } from '@store/api/authEndpoints';
import { useGetConfigQuery } from '@store/api/rootEndpoints';
import { useAddUserMutation, useUpdateUserMutation, type User } from '@store/api/userEndpoints';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { UserPlus, XIcon } from 'lucide-react';
import React, { useEffect, useId, useRef, useState } from 'react';
import { z } from 'zod';
import PasswordField from './PasswordField';
import { passwordRules } from './passwordRules';
import UserAvatar from './UserAvatar';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser?: User | null;
}

/** In the order the dialog offers them. */
const roles = ['user', 'admin'] as const;
type UserRole = typeof roles[number];

// Every message is a localization key: the schemas are made before any language is.
const addUserSchema = z.object({
  name: z.string()
    .min(2, 'User_Name_TooShort'),
  username: z.string()
    .min(3, 'User_Username_TooShort'),
  password: z.string()
    .refine((password) => passwordRules.every((rule) => rule.test(password)), 'User_Password_NotValid'),
  role: z.enum(roles),
  // How the account is found the first time its owner signs in through the identity provider.
  email: z.string().email('User_Email_Invalid').max(254, 'User_Email_Invalid').optional(),
});

/**
 * With single sign-on on and accounts found by their e-mail (`linkExisting`), an account may go
 * without a password: it is reached through the provider, which finds it by its e-mail. The
 * server decides; this only spares a round trip.
 */
const addSsoUserSchema = addUserSchema
  .extend({ password: addUserSchema.shape.password.optional() })
  .refine((user) => Boolean(user.password) || Boolean(user.email), {
    message: 'User_Email_NeededWithoutPassword', path: ['email']
  });

const updateUserSchema = addUserSchema
  .pick({ name: true, password: true, email: true })
  .extend({ password: addUserSchema.shape.password.optional() });

type FieldName = 'name' | 'username' | 'email' | 'password';

/** The fields in the order they stand, the first with an error taking the keyboard on a send. */
const fieldOrder: FieldName[] = ['name', 'username', 'email', 'password'];

/**
 * A username suggested from a name, as a directory makes one: "Ayşe Yılmaz" becomes
 * `ayse.yilmaz`. Lower-cased the Turkish way, so that "I" and "İ" both come out as "i", and
 * stripped of every other accent.
 */
const usernameFrom = (name: string) => name
  .toLocaleLowerCase('tr')
  .replace(/ı/g, 'i')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .split(/\s+/)
  .join('.')
  .replace(/[^a-z0-9._-]/g, '')
  // A name in another script leaves only the dots between its words: no suggestion then.
  .replace(/^[._-]+|[._-]+$/g, '');

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, selectedUser }) => {
  const [role, setRole] = useState<UserRole>('user');
  const [name, setName] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isUpdateForm, setUpdateForm] = useState<boolean>(false);
  // A field says what is wrong with it once it has been left, and every field once the form
  // has been sent: not while it is being typed for the first time.
  const [touched, setTouched] = useState<ReadonlySet<FieldName>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  // A new account's username follows its name until somebody types one.
  const [usernameEdited, setUsernameEdited] = useState(false);
  const { t } = useLocalization();
  const formId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const sso = useGetConfigQuery().data?.sso;
  const isSsoOn = sso?.enabled ?? false;
  // Given to whoever defines accounts, and only while single sign-on is on.
  const linkExisting = useAppSelector((state) => state.auth.user?.ssoAccounts?.linkExisting) ?? false;
  const passwordOptional = isSsoOn && linkExisting;
  // The provider's to decide, and the server refuses a change: shown, not offered.
  const isSsoGoverned = Boolean(selectedUser?.ssoGoverned);
  // A password this account cannot sign in with now, kept for when it can again.
  const passwordUnused = isSsoGoverned ? 'User_Password_Unused_SsoManaged'
    : isUpdateForm && sso?.passwordLogin === 'sysadmin_only' ? 'User_Password_Unused_PasswordsOff' : null;

  // The sysadmin may have changed `linkExisting` since the session was read.
  useEffect(() => {
    if (isOpen) void dispatch(authApiSlice.endpoints.refreshSession.initiate(undefined, { subscribe: false, forceRefetch: true }));
  }, [dispatch, isOpen]);

  const [addUser, { isLoading: isAddUserLoading }] = useAddUserMutation();
  const [updateUser, { isLoading: isUpdateLoading }] = useUpdateUserMutation();
  const isLoading = isAddUserLoading || isUpdateLoading;

  // Filled from the account being edited, and emptied when there is none: the Users page sets
  // the account as soon as a row's menu opens, and Create opened with that account's fields.
  useEffect(() => {
    const isUpdateForm = !!selectedUser;

    setUpdateForm(isUpdateForm);
    setName(selectedUser?.name ?? '');
    setRole(selectedUser?.role === 'admin' ? 'admin' : 'user');
    setUsername(selectedUser?.username ?? '');
    setEmail(selectedUser?.email ?? '');
    setPassword('');
    setTouched(new Set());
    setSubmitted(false);
    setUsernameEdited(false);
  }, [selectedUser]);

  // Checked on every render, so a field's error comes and goes as it is put right.
  const createValidation = (passwordOptional ? addSsoUserSchema : addUserSchema).safeParse({
    role,
    name,
    username,
    password: password || (passwordOptional ? undefined : ''),
    email: email.trim() || undefined
  });
  const updateValidation = updateUserSchema.safeParse({
    name,
    password: password || undefined,
    email: email.trim() || undefined,
  });
  const validation = isUpdateForm ? updateValidation : createValidation;
  const fieldErrors: Partial<Record<FieldName, string[]>> = validation.success ? {} : validation.error.flatten().fieldErrors;

  const errorOf = (field: FieldName) => {
    const message = fieldErrors[field]?.[0];

    return message && (submitted || touched.has(field)) ? t(message) : undefined;
  };

  const touch = (field: FieldName) => setTouched((current) => new Set(current).add(field));

  /** A send with errors: every error shows, and the first field with one takes the keyboard. */
  const showErrors = () => {
    setSubmitted(true);

    const first = fieldOrder.find((field) => fieldErrors[field]);
    if (first) document.getElementById(`${formId}-${first}`)?.focus();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!isUpdateForm && !usernameEdited) setUsername(usernameFrom(value));
  };

  // Emptied, the username follows the name again.
  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setUsernameEdited(value !== '');
  };

  const handleAddUser = async () => {
    if (isLoading) return;

    if (!createValidation.success) {
      showErrors();
      return;
    }

    const result = await addUser(createValidation.data);

    if (!result.error) handleClose();
  };

  const handleUpdateUser = async () => {
    if (isLoading || !selectedUser) return;

    if (!updateValidation.success) {
      showErrors();
      return;
    }

    // The address goes only when it was changed, and as `null` when it was taken away; the
    // role only when it was changed. Of an account single sign-on manages only the password.
    const { email: newEmail, name: newName, password: newPassword } = updateValidation.data;
    const emailChange = (newEmail ?? '') === (selectedUser.email ?? '') ? {} : { email: newEmail ?? null };
    const roleChange = role === selectedUser.role ? {} : { role };

    const result = await updateUser(isSsoGoverned
      ? { id: selectedUser.id, password: newPassword }
      : { id: selectedUser.id, name: newName, password: newPassword, ...emailChange, ...roleChange });

    if (!result.error) handleClose();
  };

  const handleClose = () => {
    if (isLoading) return;

    clearForm();
    onClose();
  };

  const clearForm = () => {
    setRole('user');
    setName('');
    setPassword('');
    setUsername('');
    setEmail('');
    setTouched(new Set());
    setSubmitted(false);
    setUsernameEdited(false);
  };

  // Enter sends the form, as the button does.
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void (isUpdateForm ? handleUpdateUser() : handleAddUser());
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby={`${formId}-title`}
      slotProps={{
        paper: { sx: { ...formDialogPaperStyles, width: 480 } },
        // Focused once the dialog has opened, as the folder dialog does, rather than with `autoFocus`.
        transition: { onEntered: () => nameRef.current?.focus() },
      }}
    >
      <form noValidate onSubmit={handleSubmit}>
        <Box sx={formDialogHeaderStyles}>
          {/* The account as the list draws it; a new one has no face yet. */}
          {selectedUser
            ? <UserAvatar name={selectedUser.name} size={28} />
            : <Box sx={newUserIconStyles}><UserPlus size={16} /></Box>}
          <Typography id={`${formId}-title`} component="h2" sx={formDialogTitleStyles}>
            {t(isUpdateForm ? 'User_Update' : 'User_Create')}
          </Typography>
          <IconButton aria-label={t('Common_Close')} onClick={handleClose} disabled={isLoading} sx={formDialogCloseStyles}>
            <XIcon size={18} />
          </IconButton>
        </Box>
        {/* As much room under the last field as over the first: the folder dialog gets it from the
            line it keeps for its field's error, which these fields do not keep. */}
        <Box sx={{ ...formDialogBodyStyles, display: 'flex', flexDirection: 'column', gap: 2, pb: 2.5 }}>
          <Box>
            <FormLabel htmlFor={`${formId}-name`} required sx={formDialogLabelStyles}>{t('Common_Name')}</FormLabel>
            <TextField
              id={`${formId}-name`}
              inputRef={nameRef}
              fullWidth
              size="small"
              // Somebody else's: the browser's own name and address are not offered here.
              autoComplete="off"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => touch('name')}
              error={Boolean(errorOf('name'))}
              helperText={errorOf('name')}
              disabled={isSsoGoverned || isLoading}
              sx={formDialogFieldStyles}
            />
          </Box>
          <Box>
            <FormLabel htmlFor={`${formId}-username`} required={!isUpdateForm} sx={formDialogLabelStyles}>
              {t('Common_Username')}
            </FormLabel>
            <TextField
              id={`${formId}-username`}
              fullWidth
              size="small"
              // Nor the admin's own username, which a password manager would fill in beside the password.
              autoComplete="off"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              onBlur={() => touch('username')}
              error={Boolean(errorOf('username'))}
              helperText={errorOf('username')}
              disabled={isUpdateForm || isLoading}
              sx={formDialogFieldStyles}
            />
          </Box>
          <Box>
            <FormLabel htmlFor={`${formId}-email`} sx={formDialogLabelStyles}>{t('User_Email')}</FormLabel>
            <TextField
              id={`${formId}-email`}
              fullWidth
              size="small"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => touch('email')}
              error={Boolean(errorOf('email'))}
              helperText={errorOf('email') ?? (isSsoOn ? t('User_Email_Description') : undefined)}
              disabled={isSsoGoverned || isLoading}
              sx={formDialogFieldStyles}
            />
          </Box>
          <PasswordField
            id={`${formId}-password`}
            label={t('Common_Password')}
            required={!isUpdateForm && !passwordOptional}
            value={password}
            onChange={setPassword}
            onBlur={() => touch('password')}
            error={errorOf('password')}
            note={passwordUnused ? t(passwordUnused) : undefined}
            showRules={!isUpdateForm || password !== ''}
            disabled={isLoading}
          />
          <Box>
            <FormLabel id={`${formId}-role`} sx={formDialogLabelStyles}>{t('Common_Role')}</FormLabel>
            {/* Two choices, both in view and each saying what it gives. */}
            <RadioGroup
              aria-labelledby={`${formId}-role`}
              value={role}
              onChange={(e) => setRole(e.target.value === 'admin' ? 'admin' : 'user')}
              sx={roleGroupStyles}
            >
              {roles.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  disabled={isSsoGoverned || isLoading}
                  control={<Radio size="small" />}
                  label={
                    <>
                      <Box component="span" sx={{ fontSize: 14, fontWeight: 500 }}>{t(`User_Role_${option.capitalize()}`)}</Box>
                      <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.4, color: 'text.secondary' }}>
                        {t(`User_Role_${option.capitalize()}_Desc`)}
                      </Box>
                    </>
                  }
                  className={role === option ? 'is-on' : undefined}
                  sx={roleOptionStyles}
                />
              ))}
            </RadioGroup>
            {isSsoGoverned && <FormHelperText>{t('User_SsoManaged_Locked')}</FormHelperText>}
          </Box>
        </Box>
        <Box sx={formDialogFooterStyles}>
          <Button onClick={handleClose} disabled={isLoading} sx={formDialogCancelStyles}>
            {t('Common_Cancel')}
          </Button>
          <Button type="submit" loading={isLoading} sx={formDialogSubmitStyles}>
            {t(isUpdateForm ? 'Common_Save' : 'Common_Create')}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

/** Where an account's face stands in the header, the mark of a new one. */
const newUserIconStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  color: 'primary.light',
  backgroundColor: 'action.selected',
};

const roleGroupStyles = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 1,
};

/** Each role a card with the fields' frame, the one chosen framed in the page's blue. */
const roleOptionStyles = {
  m: 0,
  p: 1.25,
  alignItems: 'flex-start',
  border: '1px solid',
  borderColor: dialogFrame,
  borderRadius: '8px',
  '&.is-on': {
    borderColor: 'primary.main',
    backgroundColor: alpha('#fff', 0.03),
  },
  '& .MuiRadio-root': { p: 0, mr: 1, mt: '2px' },
  '& .MuiFormControlLabel-label': { display: 'flex', flexDirection: 'column', gap: 0.25 },
};

export default UserFormModal;
